import { classOf, DIFFICULTY_NAMES, MODE_NAMES, type Difficulty, type Mode } from "@eldrun/content";

export interface StatsLike {
  kills: number;
  deaths: number;
  revives: number;
  dealt: number;
  taken: number;
  simpatias: number;
  chests: number;
  coins: number;
  raft: number;
  whirlwinds: number;
  pranks: number;
  /** Classes (F71). */
  blocked?: number;
  healed?: number;
  elos?: number;
}

export interface EndInput {
  endReason: string;
  /** Treta: vencedor (um) ou empatados (vários). */
  winners?: readonly string[];
  mode: string;
  difficulty: string;
  seedCode: string;
  matchSeconds: number;
  day: number;
  players: Iterable<[string, { name: string; classId?: string }]>;
  stats: { get(id: string): StatsLike | undefined };
}

export interface EndView {
  title: string;
  summary: [string, string][];
  /** Colunas da tabela e uma linha por jogador. */
  columns: string[];
  rows: { name: string; values: number[] }[];
}

const RESULTS: Record<string, string> = { vitoria: "Vitória", derrota: "Derrota", abandono: "Abandono" };

/** Tempo em m:ss. */
export const clockText = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

/**
 * Tela de fim (doc 01, Vitória, derrota e fluxo de fim): resumo do grupo (resultado, tempo, dia,
 * dificuldade, jogadores, seed) e a tabela de cada jogador (abates, mortes, revividas, dano causado
 * e recebido, Simpatias, baús, tostões, Jangada, redemoinhos, travessuras).
 */
export function endView(input: EndInput): EndView {
  const players = [...input.players];
  const names = (input.winners ?? []).map((id) => players.find(([pid]) => pid === id)?.[1].name ?? "alguém");
  const treta = input.endReason === "treta";
  const tretaResult = names.length > 1 ? `Empate: ${names.join(" e ")}` : `Vitória de ${names[0] ?? "ninguém"}`;
  const zero: StatsLike = { kills: 0, deaths: 0, revives: 0, dealt: 0, taken: 0, simpatias: 0, chests: 0, coins: 0, raft: 0, whirlwinds: 0, pranks: 0, blocked: 0, healed: 0, elos: 0 };
  const keys: (keyof StatsLike)[] = ["kills", "deaths", "revives", "dealt", "taken", "blocked", "healed", "elos", "simpatias", "chests", "coins", "raft", "whirlwinds", "pranks"];
  return {
    title: input.endReason === "vitoria" ? "Vitória!" : treta ? (names.length > 1 ? "Empate!" : `${names[0] ?? "Alguém"} venceu a Arena!`) : "Fim de partida",
    summary: [
      ["Resultado", treta ? tretaResult : (RESULTS[input.endReason] ?? "Fim")],
      ["Tempo", clockText(input.matchSeconds)],
      ["Dia alcançado", String(input.day)],
      ["Modo", MODE_NAMES[input.mode as Mode] ?? input.mode],
      ["Dificuldade", DIFFICULTY_NAMES[input.difficulty as Difficulty] ?? input.difficulty],
      ["Jogadores", String(players.length)],
      ["Seed", input.seedCode],
    ],
    columns: ["Abates", "Mortes", "Revividas", "Dano causado", "Dano recebido", "Bloqueado", "Cura dada", "Elos", "Bênçãos", "Baús", "Moedas", "Barco", "Redemoinhos", "Travessuras"],
    // O nome com a classe (F71): a tabela compara os papéis.
    rows: players.map(([id, p]) => {
      const s = input.stats.get(id) ?? zero;
      return { name: p.classId !== undefined ? `${p.name} · ${classOf(p.classId).name}` : p.name, values: keys.map((k) => s[k] ?? 0) };
    }),
  };
}
